import { generateId } from "@/lib/utils";
import { ChatMessage, ChatMessageData, DataResponse } from "@/services/promptEditor.service";
import dayjs from "dayjs";
import { ChatDatum, CompletionDatum } from "../PlaygroundData";
import { AgentLog, PromptLog } from "@/types/app/log";
import { isString } from "lodash";

export const getLatestDatumTimestamp = (datums: DataResponse[]): number => {
  return Math.max(...datums.map((datum) => dayjs.utc(datum.created_at).unix()));
};

/**
 * Converts a list of `Log`s received from the backend to a
 * list of `ChatDatum`s or `CompletionDatum`s for use in the prompt editor.
 */
export const logsToDatums = (logs: (AgentLog | PromptLog)[] | null): ChatDatum[] | CompletionDatum[] | null => {
  if (logs === null) {
    return null;
  }

  const isChat = logs.some(
    (log) => ("agent" in log && log.agent.endpoint === "chat") || ("prompt" in log && log.prompt.endpoint === "chat"),
  );

  if (isChat) {
    return logs.map((log) => {
      const messages = log.messages || [];

      // Strip template messages from the messages list.
      const templateMessages = "prompt" in log ? log.prompt.template : log.agent.template;
      const filteredMessages =
        isString(templateMessages) || !templateMessages ? messages : messages.slice(templateMessages.length);

      return {
        type: "chat",
        id: log.id || generateId("datum"),
        inputs: log.inputs || {},
        messages: filteredMessages,
        status: "not_run",
        streamSource: null,
        error: null,
        tool_results: [],
        session_reference_id: generateId("editor_session"),
        outputMessages: log.output_message ? [log.output_message] : [],
        continueMessages: [],
      } as ChatDatum;
    });
  } else {
    return logs.map((log) => {
      return {
        type: "completion",
        id: log.id || generateId("datum"),
        logId: log.id,
        inputs: log.inputs || {},
        output: log.output,
        raw_output: log.raw_output,
        status: "not_run",
        streamSource: null,
        error: null,
        tool_results: [],
      } as CompletionDatum;
    });
  }
};

/**
 * If the chat template is present, we need to remove it from the messages
 * to avoid duplication.
 * We could do `messages.slice(chatTemplate.length)`, but we need to handle the case
 * where the Log's messages list does not contain the chat template (due to user misunderstanding).
 * In this case, we should just return the messages as is.
 */
export const getMessagesAfterTemplate = (
  messages: ChatMessage[] | null,
  chatTemplate: ChatMessageData[] | null,
): ChatMessage[] | null => {
  if (messages === null) return null;
  if (chatTemplate === null || chatTemplate.length === 0) return messages;

  // If the messages list contains the chat template, we remove it.
  if (
    chatTemplate.every((m, i) => {
      const message = messages[i];
      if (!message) return false;
      if (message.role !== m.role) return false;
      if (message.content === m.content) return true;
      // If the message contains inputs, we can't compare the content directly.
      // We could replace the inputs with a wildcard and try and match it that way,
      // but it's complicated.
      // Hence we just assume they're the same for now. This is an improvement on current behaviour.
      return true;
    })
  ) {
    return messages.slice(chatTemplate.length);
  }
  // Otherwise, we return the messages as is.
  return messages;
};
